import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useHistory, useLocation } from 'react-router-dom';

import { Alert, Loading } from '../../components';
import { Badge, Icon, NumberInput, PageHeader, PageLayout, SearchInput, Segmented, Select, StatePanel, Tabs } from '../../components/ui';
import { UserContext } from '../../contexts';
import DaoListingsTab from './DaoListingsTab';
import LiveNowPanel from './LiveNowPanel';
import { WINDOWS, audienceOf, filterAndSort, flagsFor, fmtWhen, initials, median, pageLabel, roleLabel, timeAgo, typeLabel, unusualFor } from './usageInsights';

import './usage-analytics.scss';

// "Usage Analytics" — Scraping Detection Alerts & User Analytics, master admins
// only. Live now (who is online, across stores), headline numbers, and every
// active user for the window with the signals that matter; a row opens that
// user's report (UsageUserReportPage). Backend: GET /api/admin/usage-analytics
// (+ /live), retail-circle.com .claude/features/usage-analytics.md.

const AUDIENCES = [
  { value: 'all', label: 'Everyone' },
  { value: 'merchant', label: 'Merchants' },
  { value: 'client', label: 'Clients' },
  { value: 'team', label: 'RC team' },
];

// Select shows its placeholder for an empty value, so "all" needs a real one.
const ALL_FEATURES = '__all';

const COLUMNS = [
  { key: 'calls', label: 'API calls', num: true },
  { key: 'calls_per_day', label: 'Per active day', num: true },
  { key: 'active_days', label: 'Active days', num: true },
  { key: 'peak_per_minute', label: 'Busiest minute', title: 'Most requests in one minute, against the per-user limit' },
  { key: 'records_viewed', label: 'Records viewed', num: true, title: 'Distinct listings, products, orders… opened — a catalogue-walking signal' },
  { key: 'last_page', label: 'Last page', title: 'The last screen they had open in the web app' },
  { key: 'last_otp_login_at', label: 'Last OTP login', title: 'Most recent successful sign-in with a one-time code (any time, not just this window)' },
  { key: 'last_seen', label: 'Last API request', title: 'Their most recent API call in this window' },
];

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-GB'));

const TABS = [
  { value: 'users', label: 'Users', icon: <Icon name="Users" size="sm" /> },
  { value: 'dao', label: 'DAO Watches', icon: <Icon name="Eye" size="sm" /> },
];

export default function UsageAnalyticsPage() {
  const { user } = useContext(UserContext);
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const tab = params.get('tab') === 'dao' ? 'dao' : 'users';
  const initialDays = params.get('days');
  const [days, setDays] = useState(['1', '7', '30'].includes(initialDays) ? initialDays : '7');
  const [feature, setFeature] = useState(ALL_FEATURES);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [audience, setAudience] = useState('all');
  const [query, setQuery] = useState('');
  const [minCalls, setMinCalls] = useState(0);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [sort, setSort] = useState({ key: 'calls', dir: 'desc' });

  const allowed = !!user?.is_master_admin;

  useEffect(() => {
    if (!allowed || tab !== 'users') return undefined;
    let live = true;
    setLoading(true);
    setError('');
    axios.get('/api/admin/usage-analytics', { params: { days, feature: feature === ALL_FEATURES ? undefined : feature } })
      .then((res) => live && setReport(res.data))
      .catch((e) => live && setError(e.response?.data?.message || e.message))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [days, feature, allowed, tab]);

  const users = useMemo(() => report?.users || [], [report]);
  const limits = report?.limits;

  const rows = useMemo(
    () => filterAndSort(users, { audience, query, minCalls, flaggedOnly, sort: sort.key, dir: sort.dir }, limits),
    [users, audience, query, minCalls, flaggedOnly, sort, limits]
  );

  if (!user) return <Loading />;
  if (!allowed) {
    return <PageLayout><StatePanel state="permission" headingLevel={2} /></PageLayout>;
  }

  const team = users.filter((u) => audienceOf(u) === 'team');
  const merchants = users.filter((u) => audienceOf(u) === 'merchant');
  const clients = users.filter((u) => audienceOf(u) === 'client');
  const totalCalls = users.reduce((a, u) => a + u.calls, 0);
  const flagged = users.filter((u) => flagsFor(u, limits).some((f) => f.key === 'limited' || f.key === 'near'));
  const hits = report?.rate_limit_hits || [];
  const medGroups = [
    { key: 'merchant', label: 'Merchants', users: merchants },
    { key: 'client', label: 'Clients', users: clients },
    { key: 'team', label: 'RC team', users: team },
  ].map((g) => ({ ...g, value: Math.round(median(g.users.map((u) => u.calls_per_day))) }));
  const medMax = Math.max(1, ...medGroups.map((g) => g.value));
  const maxCalls = Math.max(1, ...rows.map((u) => u.calls));
  const near = limits?.near_limit_pct ?? 80;
  const windowLabel = WINDOWS.find((w) => w.value === days)?.label;

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));
  const openUser = (id) => history.push(`/usage-analytics/users/${id}?days=${days}`);

  const featureOptions = [{ value: ALL_FEATURES, label: 'All features' }].concat(
    (report?.features || []).map((f) => ({ value: f.feature, label: `${f.feature} · ${fmt(f.calls)}` }))
  );

  return (
    <PageLayout className="ua-page">
      <PageHeader
        title="Usage Analytics"
        description="Who is using Retail Circle, how much, and who is pressing against the rate limits."
        showBackButton={false}
        actions={[]}
      />

      <Tabs
        aria-label="Usage Analytics sections"
        items={TABS}
        value={tab}
        onChange={(v) => history.replace({ search: v === 'dao' ? '?tab=dao' : '' })}
      />

      {tab === 'dao' ? <DaoListingsTab /> : (
      <>
      <LiveNowPanel />

      <div className="ua-toolbar">
        <Segmented label="Window" size="sm" options={WINDOWS} value={days} onChange={setDays} />
        <Segmented label="Audience" size="sm" options={AUDIENCES} value={audience} onChange={setAudience} />
        <div className="ua-toolbar__search">
          <SearchInput aria-label="Search users" placeholder="Search name, email, store or id" value={query} size="md" onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div className="ua-toolbar__feature">
          <Select label="Feature" options={featureOptions} value={feature} onChange={(v) => setFeature(v || ALL_FEATURES)} />
        </div>
        <label className="ua-toolbar__min">
          <span>Min calls</span>
          <NumberInput aria-label="Minimum API calls" size="md" value={minCalls} min={0} onChange={(v) => setMinCalls(v || 0)} />
        </label>
        <button type="button" className={`ua-chip${flaggedOnly ? ' is-on' : ''}`} aria-pressed={flaggedOnly} onClick={() => setFlaggedOnly((f) => !f)}>
          <Icon name="AlertTriangle" size="sm" /> Flagged only
        </button>
      </div>

      {error && <Alert type="error" message="Could not load usage analytics" description={error} />}
      {loading && <Loading />}

      {!loading && report && (
        <>
          <div className="ua-tiles">
            <div className="ua-tile">
              <span>Active users · {windowLabel}</span>
              <strong>{fmt(users.length)}</strong>
              <span className="ua-split" aria-hidden="true">
                <i className="ua-fill-merchant" style={{ flexGrow: merchants.length || 0.0001 }} />
                <i className="ua-fill-customer" style={{ flexGrow: clients.length || 0.0001 }} />
                <i className="ua-fill-team" style={{ flexGrow: team.length || 0.0001 }} />
              </span>
              <em><i className="ua-dot ua-fill-merchant" /> {fmt(merchants.length)} merchants <i className="ua-dot ua-fill-customer" /> {fmt(clients.length)} clients <i className="ua-dot ua-fill-team" /> {fmt(team.length)} team</em>
            </div>
            <div className="ua-tile">
              <span>API calls · {windowLabel}</span>
              <strong>{fmt(totalCalls)}</strong>
              <em>{fmt(users.length ? Math.round(totalCalls / users.length) : 0)} per active user</em>
            </div>
            <div className="ua-tile">
              <span>Median calls per active day</span>
              <div className="ua-compare">
                {medGroups.map((g) => (
                  <React.Fragment key={g.key}>
                    <span>{g.label}</span>
                    <span className="ua-compare__bar"><i className={g.key === 'team' ? 'ua-fill-team' : g.key === 'merchant' ? 'ua-fill-merchant' : 'ua-fill-customer'} style={{ width: `${(g.value / medMax) * 100}%` }} /></span>
                    <b>{fmt(g.value)}</b>
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className={`ua-tile${flagged.length || hits.length ? ' is-alert' : ''}`}>
              <span>Needs attention</span>
              <strong>{fmt(flagged.length)}</strong>
              <em>{flagged.length === 1 ? 'user' : 'users'} near or over {fmt(limits?.api_per_minute_user)}/min · {fmt(hits.length)} rate-limit hit{hits.length === 1 ? '' : 's'}</em>
            </div>
          </div>

          {rows.length === 0 ? (
            <p className="ua-empty">No users match these filters.</p>
          ) : (
            <div className="ua-scroll">
              <table className="ua-table ua-table--users" aria-label="Users">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Platform</th>
                    <th>Store</th>
                    {COLUMNS.map((c) => (
                      <th key={c.key} title={c.title} className={c.num ? 'ua-num' : undefined} aria-sort={sort.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}>
                        <button type="button" className="ua-sort" onClick={() => toggleSort(c.key)}>
                          {c.label}{sort.key === c.key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                        </button>
                      </th>
                    ))}
                    <th><span className="ua-visually-hidden">Open report</span></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((u) => {
                    const flags = flagsFor(u, limits);
                    const unusual = unusualFor(u, limits);
                    const peak = Math.min(100, u.peak_pct_of_limit ?? 0);
                    return (
                      <tr key={u.user_id} className="ua-row" onClick={() => openUser(u.user_id)}>
                        <td>
                          <button type="button" className="ua-user" onClick={(e) => { e.stopPropagation(); openUser(u.user_id); }}>
                            <span className={`ua-avatar${u.is_team ? ' is-team' : ''}`} aria-hidden="true">{initials(u.name)}</span>
                            <span className="ua-user__text">
                              <span className="ua-user__name">
                                {u.name || `User #${u.user_id}`}
                                {unusual && (
                                  <span className={`ua-unusual ua-unusual--${unusual.tone}`} role="img" aria-label={unusual.label} title={unusual.label}>
                                    <Icon name="Flag" size="sm" />
                                  </span>
                                )}
                                <Badge tone={u.is_team ? 'accent' : 'muted'} size="sm">{typeLabel(u)}</Badge>
                              </span>
                              <span className="ua-user__meta">{u.email}</span>
                              {flags.length > 0 && <span className="ua-flags">{flags.map((f) => <Badge key={f.key} tone={f.tone} size="sm">{f.label}</Badge>)}</span>}
                            </span>
                          </button>
                        </td>
                        <td>
                          <span className="ua-platforms">
                            {(u.platforms || []).length === 0 ? '—' : u.platforms.map((p) => (
                              <span key={p.name} className={`ua-platform${p.inferred ? ' is-inferred' : ''}`} title={p.inferred ? 'From account access — no request with a site recorded yet' : `${fmt(p.calls)} calls`}>{p.name}</span>
                            ))}
                          </span>
                        </td>
                        <td className="ua-store">
                          {(u.store_seats || []).length > 0 ? (
                            <span className="ua-seats">
                              {u.store_seats.map((s) => (
                                <span key={s.store_id} className="ua-seat">
                                  <b>{s.store_name || `Store #${s.store_id}`}</b>
                                  <small>{roleLabel(s.role)}</small>
                                </span>
                              ))}
                            </span>
                          ) : (u.store_name || '—')}
                        </td>
                        <td className="ua-num">
                          <span className="ua-callbar"><b>{fmt(u.calls)}</b><span><i style={{ width: `${(u.calls / maxCalls) * 100}%` }} /></span></span>
                        </td>
                        <td className="ua-num">{fmt(Math.round(u.calls_per_day))}</td>
                        <td className="ua-num">{fmt(u.active_days)}<small> / {days}</small></td>
                        <td>
                          <span className="ua-peak" title={`${u.peak_per_minute} requests in one minute — ${u.peak_pct_of_limit ?? 0}% of the limit`}>
                            <span className="ua-meter"><i className={peak >= near ? 'is-hot' : undefined} style={{ width: `${Math.max(peak, 2)}%` }} /></span>
                            <span className="ua-peak__label">{fmt(u.peak_per_minute)}<small>/min · {u.peak_pct_of_limit ?? 0}%</small></span>
                          </span>
                        </td>
                        <td className="ua-num">{fmt(u.records_viewed)}</td>
                        <td title={u.last_page ? `${u.last_page} · ${fmtWhen(u.last_page_at)}` : 'No web-app page recorded in this window'}>
                          {u.last_page ? (
                            <span className="ua-page-cell"><b>{pageLabel(u.last_page)}</b><small>{timeAgo(u.last_page_at)}</small></span>
                          ) : '—'}
                        </td>
                        <td className="ua-nowrap" title={u.last_otp_login_at ? fmtWhen(u.last_otp_login_at) : 'No OTP sign-in on record'}>
                          {u.last_otp_login_at ? timeAgo(u.last_otp_login_at) : '—'}
                        </td>
                        <td className="ua-nowrap" title={fmtWhen(u.last_seen)}>{timeAgo(u.last_seen)}</td>
                        <td className="ua-chevron" aria-hidden="true"><Icon name="ChevronRight" size="sm" /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {hits.length > 0 && (
            <section className="ua-hits" aria-labelledby="ua-hits-title">
              <h2 id="ua-hits-title" className="ua-subhead">Rate-limit hits · {hits.length}</h2>
              <div className="ua-scroll">
                <table className="ua-table" aria-label="Rate-limit hits">
                  <thead><tr><th>Who</th><th className="ua-num">Minutes blocked</th><th>Last</th><th>User agent</th></tr></thead>
                  <tbody>
                    {hits.map((h) => (
                      <tr key={`${h.type}:${h.identifier}`}>
                        <td>{h.user_id ? (h.name || `User #${h.user_id}`) : `Guest IP ${h.ip_address || ''}`} {h.is_team && <Badge tone="accent" size="sm">Team</Badge>}</td>
                        <td className="ua-num">{fmt(h.minutes_blocked)}</td>
                        <td className="ua-nowrap" title={fmtWhen(h.last_at)}>{timeAgo(h.last_at)}</td>
                        <td><code>{h.user_agent || '—'}</code></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <p className="ua-footnote">Updated {fmtWhen(report.generated_at)} · refreshes every 5 minutes</p>
        </>
      )}
      </>
      )}
    </PageLayout>
  );
}
